import { useState } from 'react'
import { X, ChevronDown, ChevronUp, Swords, Shield, Target, Map, Skull, Wrench, Globe } from 'lucide-react'
import { UnitSprite } from './sprites'
import { StructureSprite } from './sprites'
import type { UnitType, StructureType } from '@territory-nexus/shared'
import { RESOURCE_CONFIG, UNIT_META, STRUCTURE_META } from '@territory-nexus/shared'

interface InfoPanelProps {
  onClose: () => void
}

type Section = 'overview' | 'zones' | 'units' | 'structures' | 'resources' | 'combat' | 'pve' | 'mods'

// Unit roles for the game guide (supplements shared UNIT_META)
const UNIT_ROLES: Record<UnitType, { role: string; description: string; unlock?: string }> = {
  scout_drone: { role: 'Recon', description: 'Fast, lightly armored reconnaissance unit. Excellent speed for quick strikes and retreats.' },
  assault_bot: { role: 'DPS', description: 'Balanced combat robot with good attack and decent defense. Your main offensive unit.' },
  heavy_mech: { role: 'Tank', description: 'Slow but heavily armored walker. Absorbs damage and protects weaker units.' },
  nano_swarm: { role: 'Support', description: 'Cloud of nanobots that can adapt to situations. Versatile but fragile.' },
  emp_specialist: { role: 'Disruptor', description: 'Disables enemy systems with electromagnetic pulses. High energy, low defense.', unlock: 'Research Lab Lv3' },
  titan_mech: { role: 'Elite', description: 'Massive war machine. Extremely powerful but very expensive to produce.', unlock: 'Research Lab Lv5' },
}

// Structure purposes for the game guide (supplements shared STRUCTURE_META)
const STRUCTURE_PURPOSES: Record<StructureType, string> = {
  laser_turret: 'Defense',
  shield_generator: 'Protection',
  stealth_field: 'Concealment',
  mining_rig: 'Economy',
  research_lab: 'Tech',
  headquarters: 'Command',
  teleport_gate: 'Mobility',
}

// Detailed structure gameplay effects for the guide
const STRUCTURE_GUIDE: Record<StructureType, string> = {
  laser_turret: 'Fires at a random enemy each round during zone defense, dealing 5 damage per level.',
  shield_generator: 'Reduces all incoming damage to your defending units by 3% per level (max 30%).',
  stealth_field: 'Reduces enemy attacker accuracy by 3% per level (max 30%), causing more misses.',
  mining_rig: 'Boosts resource generation: +10% at Lv1, +5% per additional level (up to +55% at Lv10).',
  research_lab: 'Unlocks advanced unit creation: Level 3 unlocks EMP Specialist, Level 5 unlocks Titan Mech.',
  headquarters: 'Boosts HP of all defending units by 2% per level. Cannot be demolished.',
  teleport_gate: 'Redeploy units already stationed in another gated zone without recalling them first.',
}

export default function InfoPanel({ onClose }: InfoPanelProps) {
  const [expanded, setExpanded] = useState<Section | null>('overview')

  const toggleSection = (section: Section) => {
    setExpanded(expanded === section ? null : section)
  }

  return (
    <div className="fixed inset-0 z-[102] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      {/* Panel */}
      <div className="relative w-full max-w-lg max-h-[85vh] bg-slate-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-slate-800/50">
          <h2 className="text-lg font-display font-bold text-gradient-cyan">Game Guide</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Overview Section */}
          <SectionHeader 
            title="Game Overview" 
            icon={<Map className="w-4 h-4" />}
            isExpanded={expanded === 'overview'} 
            onClick={() => toggleSection('overview')} 
          />
          {expanded === 'overview' && (
            <div className="px-4 pb-4 space-y-3 text-sm text-slate-300">
              <p>
                <strong className="text-cyan-400">Territory Nexus</strong> is a real-world location-based strategy game.
                Claim hexagonal zones in your area, build structures, deploy units, and battle other players for territorial control.
              </p>
              <div className="glass rounded-lg p-3 border border-cyan-500/20">
                <h4 className="font-semibold text-white mb-2">Core Gameplay Loop</h4>
                <ol className="list-decimal list-inside space-y-1 text-slate-400">
                  <li>Walk to unclaimed zones in the real world</li>
                  <li>Claim zones to generate resources</li>
                  <li>Build structures to enhance your zones</li>
                  <li>Create and deploy units to defend or attack</li>
                  <li>Purge corrupted zones or battle other players</li>
                </ol>
              </div>
              <div className="glass rounded-lg p-3 border border-white/5">
                <h4 className="font-semibold text-white mb-2">Map Controls</h4>
                <ul className="list-disc list-inside space-y-1 text-xs text-slate-400">
                  <li>GPS tracking is <strong className="text-cyan-400">auto-enabled</strong> — your position updates as you walk</li>
                  <li>Tap any hex on the map to select it and view its details</li>
                  <li>Selected zones highlight with a grey outline</li>
                  <li>Expand the bottom panel to see biome, modifiers, resources, units, and structures</li>
                </ul>
              </div>
              <p className="text-slate-400">
                Your faction choice affects resource generation bonuses. Work with allies to dominate regions!
              </p>
            </div>
          )}

          {/* Zones & Modifiers Section */}
          <SectionHeader
            title="Zones & Modifiers"
            icon={<Globe className="w-4 h-4" />}
            isExpanded={expanded === 'zones'}
            onClick={() => toggleSection('zones')}
          />
          {expanded === 'zones' && (
            <div className="px-4 pb-4 space-y-3">
              <p className="text-sm text-slate-400 mb-3">
                Each hex zone has a unique biome, resource profile, and may have environmental modifiers that affect gameplay.
              </p>
              <div className="glass rounded-lg p-3 border border-white/5">
                <h4 className="font-semibold text-white text-sm mb-2">Zone Biomes</h4>
                <p className="text-xs text-slate-400">
                  Every zone belongs to a biome (Verdant, Arid, Tundra, etc.) that determines its visual appearance and resource distribution.
                  Some biomes are richer in specific resources.
                </p>
              </div>
              <div className="glass rounded-lg p-3 border border-white/5">
                <h4 className="font-semibold text-white text-sm mb-2">Zone Modifiers</h4>
                <p className="text-xs text-slate-400 mb-2">
                  About 40% of zones have 1-2 environmental modifiers. These are permanent and shown as badges in the zone detail panel.
                </p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 font-medium">🕊 Sanctuary</span>
                    <span className="text-[10px] text-slate-500">PvP attacks completely blocked</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 font-medium">💎 Rich Deposit</span>
                    <span className="text-[10px] text-slate-500">+25% resource generation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 font-medium">💥 Volatile</span>
                    <span className="text-[10px] text-slate-500">+20% critical hit chance in battles</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-300 font-medium">☢ Radiation</span>
                    <span className="text-[10px] text-slate-500">All units lose 5% HP per round</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-slate-300 font-medium">🌫 Dense Fog</span>
                    <span className="text-[10px] text-slate-500">-20% accuracy, +15% dodge</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-slate-300 font-medium">🔽 High Gravity</span>
                    <span className="text-[10px] text-slate-500">-10% unit speed</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-slate-300 font-medium">🔼 Low Gravity</span>
                    <span className="text-[10px] text-slate-500">+10% unit speed</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-slate-300 font-medium">🛡 Fortified</span>
                    <span className="text-[10px] text-slate-500">+10% structure HP</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-slate-300 font-medium">🌀 Anomaly</span>
                    <span className="text-[10px] text-slate-500">Random ±15% to all stats</span>
                  </div>
                </div>
              </div>
              <div className="glass rounded-lg p-3 border border-amber-500/20">
                <h4 className="font-semibold text-amber-400 text-sm mb-1">Sanctuary Zones</h4>
                <p className="text-xs text-slate-400">
                  Zones with the Sanctuary modifier are safe from PvP attacks. You cannot attack or be attacked in these zones.
                  The Attack button is replaced with a Sanctuary label. Sanctuary is permanent and determined by the zone's location.
                </p>
              </div>
            </div>
          )}

          {/* Units Section */}
          <SectionHeader 
            title="Units" 
            icon={<Target className="w-4 h-4" />}
            isExpanded={expanded === 'units'} 
            onClick={() => toggleSection('units')} 
          />
          {expanded === 'units' && (
            <div className="px-4 pb-4 space-y-3">
              <p className="text-sm text-slate-400 mb-3">
                Units are your military force. Create them using resources, deploy them to zones for defense, or send them to attack enemies.
              </p>
              <div className="space-y-2">
                {(Object.keys(UNIT_META) as UnitType[]).map(type => {
                  const meta = UNIT_META[type]
                  const roleInfo = UNIT_ROLES[type]
                  return (
                    <div key={type} className="glass rounded-lg p-3 border border-white/5 flex items-start gap-3">
                      <UnitSprite unitType={type} level={1} status="idle" size="md" showLevel={false} animate={false} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-semibold ${meta.color}`}>{meta.name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-400">{roleInfo.role}</span>
                          {roleInfo.unlock && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">{roleInfo.unlock}</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{roleInfo.description}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="glass rounded-lg p-3 border border-amber-500/20 mt-3">
                <h4 className="font-semibold text-amber-400 text-sm mb-1">Unit Progression</h4>
                <p className="text-xs text-slate-400">
                  Units gain XP from battles. Higher levels increase all stats by 5% per level, up to level 20.
                </p>
              </div>
            </div>
          )}

          {/* Structures Section */}
          <SectionHeader 
            title="Structures" 
            icon={<Shield className="w-4 h-4" />}
            isExpanded={expanded === 'structures'} 
            onClick={() => toggleSection('structures')} 
          />
          {expanded === 'structures' && (
            <div className="px-4 pb-4 space-y-3">
              <p className="text-sm text-slate-400 mb-3">
                Build structures on your zones to enhance defense, boost resource generation, or unlock special abilities.
              </p>
              <div className="space-y-2">
                {(Object.keys(STRUCTURE_META) as StructureType[]).map(type => {
                  const meta = STRUCTURE_META[type]
                  const purpose = STRUCTURE_PURPOSES[type]
                  const guide = STRUCTURE_GUIDE[type]
                  return (
                    <div key={type} className="glass rounded-lg p-3 border border-white/5 flex items-start gap-3">
                      <StructureSprite structureType={type} level={1} size="md" showLevel={false} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-semibold ${meta.color}`}>{meta.name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-400">{purpose}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{guide}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="glass rounded-lg p-3 border border-violet-500/20 mt-3">
                <h4 className="font-semibold text-violet-400 text-sm mb-1">Upgrades</h4>
                <p className="text-xs text-slate-400">
                  Structures can be upgraded to level 10. Higher levels provide stronger bonuses but cost more resources (1.5x per level).
                  Each zone supports one of each structure type.
                </p>
              </div>
            </div>
          )}

          {/* Resources Section */}
          <SectionHeader
            title="Resources"
            icon={<span className="text-base">{RESOURCE_CONFIG.energy.symbol}</span>}
            isExpanded={expanded === 'resources'}
            onClick={() => toggleSection('resources')}
          />
          {expanded === 'resources' && (
            <div className="px-4 pb-4 space-y-3">
              <p className="text-sm text-slate-400 mb-3">
                Resources are generated passively by your owned zones. Use them to create units and build structures.
              </p>
              <div className="space-y-2">
                <ResourceCard
                  icon={<span className={`text-lg ${RESOURCE_CONFIG.energy.color}`}>{RESOURCE_CONFIG.energy.symbol}</span>}
                  name="Energy"
                  color="amber"
                  description="Basic resource. Powers everything. Generated by all zones."
                />
                <ResourceCard
                  icon={<span className={`text-lg ${RESOURCE_CONFIG.nanomaterials.color}`}>{RESOURCE_CONFIG.nanomaterials.symbol}</span>}
                  name="Nanomaterials"
                  color="emerald"
                  description="Construction material for units and structures. Rarer than energy."
                />
                <ResourceCard
                  icon={<span className={`text-lg ${RESOURCE_CONFIG.quantum.color}`}>{RESOURCE_CONFIG.quantum.symbol}</span>}
                  name="Quantum"
                  color="violet"
                  description="Advanced resource for high-tech units and structures."
                />
                <ResourceCard
                  icon={<span className={`text-lg ${RESOURCE_CONFIG.darkMatter.color}`}>{RESOURCE_CONFIG.darkMatter.symbol}</span>}
                  name="Dark Matter"
                  color="rose"
                  description="Rare exotic material. Required for elite units and special structures."
                />
              </div>
              <div className="glass rounded-lg p-3 border border-green-500/20 mt-3">
                <h4 className="font-semibold text-green-400 text-sm mb-1">Boosting Generation</h4>
                <p className="text-xs text-slate-400">
                  Build Mining Rigs to boost generation (+10% at Lv1, scaling to +55% at Lv10). Faction bonuses also affect specific resource types.
                  Each zone has a unique resource profile — some are energy-rich, others specialize in nanomaterials, quantum, or dark matter.
                </p>
              </div>
            </div>
          )}

          {/* Combat Section */}
          <SectionHeader 
            title="Combat" 
            icon={<Swords className="w-4 h-4" />}
            isExpanded={expanded === 'combat'} 
            onClick={() => toggleSection('combat')} 
          />
          {expanded === 'combat' && (
            <div className="px-4 pb-4 space-y-3">
              <p className="text-sm text-slate-400 mb-3">
                Attack enemy zones to capture territory. Combat is auto-resolved turn-by-turn with units acting in speed order.
              </p>
              <div className="glass rounded-lg p-3 border border-white/5">
                <h4 className="font-semibold text-white text-sm mb-2">Battle Flow</h4>
                <ol className="list-decimal list-inside space-y-1 text-xs text-slate-400">
                  <li>Select 1-3 idle units to send into battle</li>
                  <li>Battle auto-resolves — units attack in speed order</li>
                  <li>Each unit targets a random enemy per turn</li>
                  <li>15% base crit chance (2x damage)</li>
                  <li>Battle ends when one side is eliminated (max 30 turns)</li>
                  <li>Defender wins if battle reaches the turn limit</li>
                </ol>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="glass rounded-lg p-2 border border-red-500/20 text-center">
                  <Swords className="w-4 h-4 text-red-400 mx-auto mb-1" />
                  <div className="text-xs font-semibold text-red-400">ATK</div>
                  <div className="text-[10px] text-slate-500">Damage dealt</div>
                </div>
                <div className="glass rounded-lg p-2 border border-blue-500/20 text-center">
                  <Shield className="w-4 h-4 text-blue-400 mx-auto mb-1" />
                  <div className="text-xs font-semibold text-blue-400">DEF</div>
                  <div className="text-[10px] text-slate-500">Damage reduced</div>
                </div>
              </div>
              <div className="glass rounded-lg p-3 border border-emerald-500/20 mt-3">
                <h4 className="font-semibold text-emerald-400 text-sm mb-1">Zone Defense Bonuses</h4>
                <p className="text-xs text-slate-400">
                  Defending zones benefit from structures: Laser Turrets deal automatic damage each round,
                  Shield Generators reduce incoming damage (up to 30%), Stealth Fields lower attacker accuracy (up to 30%),
                  and HQ boosts defender HP (up to 20%).
                </p>
              </div>
              <div className="glass rounded-lg p-3 border border-slate-500/20 mt-3">
                <h4 className="font-semibold text-slate-300 text-sm mb-1">Zone Modifier Effects</h4>
                <p className="text-xs text-slate-400">
                  Environmental modifiers affect battles fought in that zone. Volatile zones increase crit chance,
                  Dense Fog reduces accuracy, Radiation drains HP each round, and gravity modifiers change speed.
                  Check zone details before attacking!
                </p>
              </div>
              <div className="glass rounded-lg p-3 border border-cyan-500/20 mt-3">
                <h4 className="font-semibold text-cyan-400 text-sm mb-1">PvP Victory Rewards</h4>
                <p className="text-xs text-slate-400">
                  Winning PvP battles awards XP to surviving units (50 base + 100 win bonus + 25 per kill),
                  a 30% chance for a mod drop, and capture of the enemy zone. Captured zones receive a 2-hour shield.
                </p>
              </div>
            </div>
          )}
          {/* PvE / AI Section */}
          <SectionHeader
            title="PvE Encounters"
            icon={<Skull className="w-4 h-4" />}
            isExpanded={expanded === 'pve'}
            onClick={() => toggleSection('pve')}
          />
          {expanded === 'pve' && (
            <div className="px-4 pb-4 space-y-3">
              <p className="text-sm text-slate-400 mb-3">
                Corrupted zones appear periodically on the map, marked in purple. Clear them to earn resources, XP, and mod drops.
              </p>
              <div className="glass rounded-lg p-3 border border-white/5">
                <h4 className="font-semibold text-white text-sm mb-2">How PvE Works</h4>
                <ol className="list-decimal list-inside space-y-1 text-xs text-slate-400">
                  <li>Corrupted zones spawn automatically every ~5 minutes (max 5 active)</li>
                  <li>Each corrupted zone has a difficulty level (1-10) affecting AI strength</li>
                  <li>Tap a corrupted zone and press "Purge" to preview its AI defenders</li>
                  <li>Select 1-3 units and initiate the attack</li>
                  <li>Battle auto-resolves using the same combat engine as PvP</li>
                  <li>Corrupted zones despawn after 2 hours if not cleared</li>
                </ol>
              </div>
              <div className="glass rounded-lg p-3 border border-white/5">
                <h4 className="font-semibold text-white text-sm mb-2">AI Difficulty Scaling</h4>
                <div className="space-y-1 text-xs text-slate-400">
                  <p><strong className="text-slate-300">Difficulty 1-9:</strong> 1 AI unit (Scout Drones, Assault Bots)</p>
                  <p><strong className="text-slate-300">Difficulty 10-19:</strong> 2 AI units (+ Nano Swarms)</p>
                  <p><strong className="text-slate-300">Difficulty 20+:</strong> 3 AI units (Heavy Mechs, EMPs, Titans)</p>
                  <p className="mt-1">AI unit levels scale with difficulty. Higher difficulty means stronger, higher-level enemies.</p>
                </div>
              </div>
              <div className="glass rounded-lg p-3 border border-purple-500/20 mt-3">
                <h4 className="font-semibold text-purple-400 text-sm mb-1">PvE Rewards</h4>
                <div className="text-xs text-slate-400 space-y-1">
                  <p><strong className="text-slate-300">Resources:</strong> Energy, nanomaterials, quantum (scaled by difficulty). Dark matter from difficulty 10+.</p>
                  <p><strong className="text-slate-300">Unit XP:</strong> 50 base + 100 win bonus + 25 per AI unit destroyed.</p>
                  <p><strong className="text-slate-300">Player XP:</strong> 100 per victory.</p>
                  <p><strong className="text-slate-300">Mod drop:</strong> 40% chance on victory (higher than PvP's 30%).</p>
                  <p className="mt-1">Cleared zones reset to neutral and become claimable.</p>
                </div>
              </div>
            </div>
          )}

          {/* Mods Section */}
          <SectionHeader
            title="Mods & Equipment"
            icon={<Wrench className="w-4 h-4" />}
            isExpanded={expanded === 'mods'}
            onClick={() => toggleSection('mods')}
          />
          {expanded === 'mods' && (
            <div className="px-4 pb-4 space-y-3">
              <p className="text-sm text-slate-400 mb-3">
                Mods are equipment pieces that enhance your units' stats. Earn them from battles and equip them for an edge in combat.
              </p>
              <div className="glass rounded-lg p-3 border border-white/5">
                <h4 className="font-semibold text-white text-sm mb-2">Mod Basics</h4>
                <ul className="list-disc list-inside space-y-1 text-xs text-slate-400">
                  <li>Each unit can equip up to 3 mods</li>
                  <li>Mods boost stats like ATK, DEF, SPD, ACC, HP, or Energy</li>
                  <li>Earned from PvP victories (30% drop chance) and PvE victories (40% drop chance)</li>
                </ul>
              </div>
              <div className="glass rounded-lg p-3 border border-white/5">
                <h4 className="font-semibold text-white text-sm mb-2">Rarity Tiers</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-slate-400" />
                    <span className="text-xs text-slate-400">Common — small boost</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="text-xs text-emerald-400">Uncommon — moderate boost</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-400" />
                    <span className="text-xs text-blue-400">Rare — significant boost</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-purple-400" />
                    <span className="text-xs text-purple-400">Epic — major boost</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-400" />
                    <span className="text-xs text-amber-400">Legendary — exceptional</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function SectionHeader({ title, icon, isExpanded, onClick }: { 
  title: string
  icon: React.ReactNode
  isExpanded: boolean
  onClick: () => void 
}) {
  return (
    <button
      onClick={onClick}
      className="w-full px-4 py-3 flex items-center justify-between bg-slate-800/30 hover:bg-slate-800/50 transition-colors border-b border-white/5"
    >
      <div className="flex items-center gap-2">
        <span className="text-cyan-400">{icon}</span>
        <span className="font-semibold text-white">{title}</span>
      </div>
      {isExpanded ? (
        <ChevronUp className="w-4 h-4 text-slate-400" />
      ) : (
        <ChevronDown className="w-4 h-4 text-slate-400" />
      )}
    </button>
  )
}

function ResourceCard({ icon, name, color, description }: {
  icon: React.ReactNode
  name: string
  color: string
  description: string
}) {
  return (
    <div className={`glass rounded-lg p-3 border border-${color}-500/20 flex items-start gap-3`}>
      {icon}
      <div>
        <span className={`font-semibold text-${color}-400`}>{name}</span>
        <p className="text-xs text-slate-400 mt-0.5">{description}</p>
      </div>
    </div>
  )
}
